import React, { useState, useRef } from 'react';
import '../../styles/Payroll.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { EmployeeService } from '../../services/payrollService';

interface ImportResult {
  success: boolean;
  message: string;
  data?: any;
  errors?: string[];
  warnings?: string[];
  imported?: number;
  failed?: number;
}

interface EmployeeImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: (result: ImportResult) => void;
}

const EmployeeImportDialog: React.FC<EmployeeImportDialogProps> = ({
  open,
  onClose,
  onImportComplete
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      handleFilePreview(selectedFile);
    }
  };

  const handleFilePreview = async (file: File) => {
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      // Call preview API endpoint
      const response = await EmployeeService.previewImport(formData);
      if (response.success) {
        setPreviewData(response.data || []);
        setStep('preview');
      } else {
        setImportResult({
          success: false,
          message: response.message || 'Failed to preview file',
          errors: response.errors
        });
        setStep('result');
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        message: error.response?.data?.message || 'Failed to preview file',
        errors: [error.message]
      });
      setStep('result');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await EmployeeService.importEmployees(formData);
      setImportResult(response);
      setStep('result');
      
      if (response.success) {
        onImportComplete(response);
      }
    } catch (error: any) {
      const result: ImportResult = {
        success: false,
        message: error.response?.data?.message || 'Import failed',
        errors: error.response?.data?.errors || [error.message]
      };
      setImportResult(result);
      setStep('result');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await EmployeeService.downloadImportTemplate();
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'employee_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setImportResult(null);
    setStep('upload');
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Import employees from an Excel file. Download the template to see the required format.
      </Typography>
      
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          Download Template
        </Button>
      </Box>

      <Box
        sx={{
          border: '2px dashed var(--payroll-neutral-300)', 
          borderRadius: 'var(--payroll-radius-md)', 
          p: 3, 
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'var(--payroll-primary-500)',
            backgroundColor: 'var(--payroll-neutral-50)'
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {file ? file.name : 'Click to select file or drag and drop'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supported formats: .xlsx, .xls, .csv
        </Typography>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Box>

      {importing && (
        <Box mt={2} className="payroll-loading">
          <LinearProgress className="payroll-progress" />
          <Typography variant="body2" color="text.secondary" align="center" mt={1}>
            Processing file...
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Preview of {previewData.length} employees to be imported:
      </Typography>
      
      <TableContainer component={Paper} className="payroll-card" sx={{ maxHeight: 400, mt: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee No</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Validation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewData.map((employee, index) => (
              <TableRow key={index}>
                <TableCell>{employee.employee_no}</TableCell>
                <TableCell>{employee.full_name}</TableCell>
                <TableCell>{employee.email}</TableCell>
                <TableCell>{employee.job_title}</TableCell>
                <TableCell>
                  <Chip 
                    label={employee.status} 
                    size="small"
                    color={employee.status === 'active' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {employee.validation_errors?.length > 0 ? (
                    <Tooltip title={employee.validation_errors.join(', ')}>
                      <ErrorIcon color="error" fontSize="small" />
                    </Tooltip>
                  ) : employee.validation_warnings?.length > 0 ? (
                    <Tooltip title={employee.validation_warnings.join(', ')}>
                      <WarningIcon color="warning" fontSize="small" />
                    </Tooltip>
                  ) : (
                    <SuccessIcon color="success" fontSize="small" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {importing && (
        <Box mt={2} className="payroll-loading">
          <LinearProgress className="payroll-progress" />
          <Typography variant="body2" color="text.secondary" align="center" mt={1}>
            Importing employees...
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderResultStep = () => (
    <Box>
      {importResult && (
        <>
          <div className={`payroll-notification ${importResult.success ? 'payroll-notification-success' : 'payroll-notification-error'} payroll-notification-mb`}>
            <div className="payroll-notification-content">
            {importResult.message}
            </div>
          </div>

          {importResult.success && (
            <Box mb={2}>
              <Typography variant="body2">
                Successfully imported: {importResult.imported || 0} employees
              </Typography>
              {importResult.failed && importResult.failed > 0 && (
                <Typography variant="body2" color="error">
                  Failed to import: {importResult.failed} employees
                </Typography>
              )}
            </Box>
          )}

          {importResult.errors && importResult.errors.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="error">
                Errors:
              </Typography>
              {importResult.errors.map((error, index) => (
                <Typography key={index} variant="body2" color="error">
                  • {error}
                </Typography>
              ))}
            </Box>
          )}

          {importResult.warnings && importResult.warnings.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="warning.main">
                Warnings:
              </Typography>
              {importResult.warnings.map((warning, index) => (
                <Typography key={index} variant="body2" color="warning.main">
                  • {warning}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="payroll-dialog"
    >
      <DialogTitle className="payroll-card-header">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" className="payroll-card-title">
            Import Employees
          </Typography>
          <IconButton onClick={handleClose} size="small" className="payroll-action-button">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className="payroll-card-body">
        {step === 'upload' && renderUploadStep()}
        {step === 'preview' && renderPreviewStep()}
        {step === 'result' && renderResultStep()}
      </DialogContent>
      
      <DialogActions className="payroll-dialog-actions">
        {step === 'upload' && (
          <Button 
            onClick={handleClose}
            className="payroll-button payroll-button-secondary"
          >
            Cancel
          </Button>
        )}
        
        {step === 'preview' && (
          <>
            <Button 
              onClick={() => setStep('upload')}
              disabled={importing}
              className="payroll-button payroll-button-secondary"
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              onClick={handleImport}
              disabled={importing || previewData.length === 0}
              className="payroll-button payroll-button-primary"
              startIcon={<UploadIcon />}
            >
              Import {previewData.length} Employees
            </Button>
          </>
        )}
        
        {step === 'result' && (
          <Button 
            variant="contained" 
            onClick={handleClose}
            className="payroll-button payroll-button-primary"
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeImportDialog;
